import { create } from "zustand";

export type TransportOrderStatus = "OPEN" | "SENT" | "ACCEPTED" | "RETRACTED" | "CHANGED" | "MOVED" | "STORNO" | "DELETED";
export type TransportStatusSource = { id: number; transportStatus?: string; status?: string };

// Status controls subscribe to their own order, independently of calendar/list queries.
const useStatuses = create<Record<number, TransportOrderStatus>>(() => ({}));
const statusRevisions = new Map<number, number>();
let revision = 0;
let minimumReadRevision = 0;
const statuses = new Set<string>(["OPEN", "SENT", "ACCEPTED", "RETRACTED", "CHANGED", "MOVED", "STORNO", "DELETED"]);

export function useTransportOrderStatus(id: number, initialStatus: TransportOrderStatus): TransportOrderStatus {
  return useStatuses((current) => current[id] ?? initialStatus);
}

export function getTransportOrderStatus(id: number, initialStatus: TransportOrderStatus): TransportOrderStatus {
  return useStatuses.getState()[id] ?? initialStatus;
}

export function updateTransportOrderStatus(id: number, status: TransportOrderStatus): void {
  statusRevisions.set(id, ++revision);
  if (useStatuses.getState()[id] !== status) useStatuses.setState({ [id]: status });
}

/** Capture before starting a read, so an older response cannot undo a later saved status. */
export const transportStatusReadRevision = () => ++revision;

export function receiveTransportOrderStatuses(orders: readonly (TransportStatusSource | null | undefined)[], readRevision: number): void {
  if (readRevision < minimumReadRevision) return;
  useStatuses.setState((current) => {
    let next = current;
    for (const order of orders) {
      const status = order?.transportStatus ?? order?.status;
      if (!order || !status || !statuses.has(status) || (statusRevisions.get(order.id) ?? 0) > readRevision) continue;
      statusRevisions.set(order.id, readRevision);
      if (current[order.id] === status) continue;
      if (next === current) next = { ...current };
      next[order.id] = status as TransportOrderStatus;
    }
    return next;
  });
}

export function resetTransportOrderStatuses(): void {
  minimumReadRevision = ++revision;
  statusRevisions.clear();
  useStatuses.setState({}, true);
}
